import axios from "axios";
import { useEffect, useState } from "react";
import { NotificationManager } from "react-notifications";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import LeftIcon from "../../assets/svgs/leftArrowIcon.svg";
import RightIcon from "../../assets/svgs/rightArrowIcon.svg";
import Loader from "../../components/Loader";
import SearchDropdownList from "../../components/SearchDropdownList";
import useAuth from "../../hooks/useAuth";
import BaseLayout from "../../layouts/BaseLayout";
import { BASE_URL } from "../../utils/Element";
import { initialBuyerFilterData } from "../../utils/initialData";
import BuyersDetailsHeader from "./components/BuyersDetailsHeader";

const BuyersDetails = () => {
  const { id } = useParams();
  const [config] = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [buyerData, setBuyerData] = useState();

  const [filterData, setFilterData] = useState(() => {
    const localFilterData = localStorage.getItem("filterData");
    return localFilterData
      ? JSON.parse(localFilterData)
      : initialBuyerFilterData;
  });

  const fetchDetails = () => {
    setLoading(true);
    const savedQueryParams = localStorage.getItem("queryParams");
    let localQueryParams = {};

    if (savedQueryParams) {
      localQueryParams = Object.fromEntries(
        new URLSearchParams(savedQueryParams)
      );
    }

    axios
      .post(`${BASE_URL}/acquisition-getbyid/${id}`, localQueryParams, config)
      .then((res) => {
        setBuyerData(res?.data?.data);
      })
      .catch((err) => {
        if (
          err.response?.data?.message &&
          err.response?.data?.message !== "Too Many Attempts."
        ) {
          NotificationManager.error(err.response?.data?.message);
        }
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchDetails();
  }, [id]);

  // ✅ reusable UI for both sections
  const renderMiles = (data) => (
    <>
      <div className="mt-3 mb-2 flex items-center gap-2">
        <p className="dark-M body-N">1 Mile</p>
        <span className="tags">{data?.oneMile || ""}</span>
      </div>
      <div className="mt-3 mb-2 flex items-center gap-2">
        <p className="dark-M body-N">3 Miles</p>
        <span className="tags">{data?.secondMile || ""}</span>
      </div>
      <div className="mt-3 mb-2 flex items-center gap-2">
        <p className="dark-M body-N">5 Miles</p>
        <span className="tags">{data?.thirdMile || ""}</span>
      </div>
    </>
  );

  const queryParams = new URLSearchParams(location.search);
  const searchValue = queryParams.get("search");

  return (
    <BaseLayout>
      {/* Top Bar */}
      <div className="flex justify-between items-center">
        <p
          role="button"
          className="green-H head-5 underline"
          onClick={() =>
            navigate(`/buyers?search=${searchValue || ""}`)
          }
        >
          Back to Buyers
        </p>

        <div className="body-S dark-M flex items-center gap-5">
          {buyerData?.previous_id !== null && (
            <div
              role="button"
              onClick={() =>
                navigate(`/buyer/${buyerData?.previous_id}`)
              }
              className="flex items-center gap-2"
            >
              <img src={LeftIcon} alt="" />
              Previous Buyer
            </div>
          )}

          {buyerData?.next_id !== null && (
            <div
              role="button"
              onClick={() =>
                navigate(`/buyer/${buyerData?.next_id}`)
              }
              className="flex items-center gap-2"
            >
              Next Buyer
              <img src={RightIcon} alt="" />
            </div>
          )}
        </div>

        <SearchDropdownList from="buyer" />
      </div>

      {/* Main */}
      <div className="contact-details light-bg-L">
        {loading ? (
          <Loader />
        ) : (
          <>
            <BuyersDetailsHeader
              buyerData={buyerData}
              onCallApi={fetchDetails}
            />

            <div className="flex justify-between mt-4">
              {/* LEFT */}
              <div className="w-[50%]">
                <p className="dark-M body-L">SEARCH CRITERIA</p>

                <div className="mt-3">
                  <p className="dark-M body-N">
                    Min Lease Term Remaining
                  </p>
                  <p className="tags green-H body-S">
                    {buyerData?.min_lease_term_reamaining
                      ? `${buyerData?.min_lease_term_reamaining} Years`
                      : "0 Year"}
                  </p>
                </div>

                <div className="mt-3">
                  <p className="dark-M body-N">
                    Availability Status
                  </p>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {buyerData?.availability_status?.map((el, i) => (
                      <p key={i} className="tags green-H body-S">
                        {el}
                      </p>
                    ))}
                  </div>
                </div>

                <div className="mt-3">
                  <p className="dark-M body-N">Property Type</p>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {buyerData?.property_type?.map((el, i) => (
                      <p key={i} className="tags green-H body-S">
                        {el?.type}
                      </p>
                    ))}
                  </div>
                </div>

                <div className="mt-3">
                  <p className="dark-M body-N">
                    Landlord Responsibilities
                  </p>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {buyerData?.landlord_responsibilities?.map(
                      (el, i) => (
                        <p
                          key={i}
                          className="tags green-H body-S"
                        >
                          {el}
                        </p>
                      )
                    )}
                  </div>
                </div>
              </div>

              {/* RIGHT */}
              <div className="w-[50%]">
                {/* Prices */}
                <div className="flex gap-16 mt-3">
                  <div>
                    <p className="dark-M body-N">Min Price</p>
                    <p className="tags green-H body-S">
                      ${buyerData?.min_price?.toLocaleString()}
                    </p>
                  </div>

                  <div>
                    <p className="dark-M body-N">Max Price</p>
                    <p className="tags green-H body-S">
                      ${buyerData?.max_price?.toLocaleString()}
                    </p>
                  </div>

                  <div>
                    <p className="dark-M body-N">
                      Asking Cap Rate
                    </p>
                    <p className="tags green-H body-S">
                      {buyerData?.min_asking_cap_rate}%
                    </p>
                  </div>
                </div>

                {/* SF */}
                <div className="flex gap-16 mt-6">
                  <div>
                    <p className="dark-M body-N">
                      Price per SF
                    </p>
                    <p className="tags green-H body-S">
                      {buyerData?.price_sf}
                    </p>

                    <p className="dark-M body-N mt-6">
                      Rent per SF
                    </p>
                    <p className="tags green-H body-S">
                      {buyerData?.rent_sf}
                    </p>
                  </div>

                  {/* ✅ PARALLEL SECTION */}
                  <div className="flex gap-16">
                    {/* Demographics */}
                    <div>
                      <p className="dark-M body-N">
                        Min. Demographics
                      </p>
                      <div className="green-H body-S">
                        {renderMiles(
                          buyerData?.min_demographics
                        )}
                      </div>
                    </div>

                    {/* Traffic */}
                    <div>
                      <p className="dark-M body-N">
                        Min. Traffic Counts
                      </p>
                      <div className="green-H body-S">
                        {renderMiles(
                          buyerData?.min_traffic_count
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* GLA */}
                <div className="mt-4">
                  <p className="dark-M body-N">Total GLA</p>
                  <p className="tags green-H body-S">
                    {buyerData?.total_gla}
                  </p>
                </div>
              </div>
            </div>

            {/* Bottom */}
            <div className="mt-4">
              <p className="dark-M body-N">Tenants</p>
              <div className="flex flex-wrap gap-2 mt-1">
                {buyerData?.tenant_details?.map((el, i) => (
                  <p key={i} className="tags green-H body-S">
                    {el?.tenant_name}
                  </p>
                ))}
              </div>

              <p className="dark-M body-N mt-3">State</p>
              <div className="flex flex-wrap gap-2 mt-1">
                {buyerData?.state?.map((el, i) => (
                  <p key={i} className="tags green-H body-S">
                    {el}
                  </p>
                ))}
              </div>

              <p className="dark-M body-N mt-3">Comments</p>
              <p className="dark-H">
                {buyerData?.comment ?? ""}
              </p>
            </div>
          </>
        )}
      </div>
    </BaseLayout>
  );
};

export default BuyersDetails;